<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row no-gutters>
        <v-col class="pa-0 text-subtitle-1 font-weight-bold" cols="auto">
          {{ $i18n.t('admin.rangesValuesInLegend') }}
        </v-col>
      </v-row>
      <v-row no-gutters class="pb-5">
        <v-col
          cols="auto"
          class="font--text pa-0 text-caption font-italic font-weight-light"
          style="height: auto; line-height: 1rem !important"
        >
          {{ $i18n.t('admin.rangesValuesInLegendSubtitle') }}
        </v-col>
      </v-row>
      <card-title-row translationPath="admin.nodes" v-bind="subTitleProps" />
      <admin-wrapper
        hide-title-row
        :disabled="loading"
        :inputs="nodesInputs"
        :hasButtons="false"
        @validate="setValidation('nodes', $event)"
      >
        <template v-for="(attribute, idx) in nodesInputs" #[attribute.nameInput]>
          <v-col cols="4" :key="idx">
            <data-input
              v-bind="attribute"
              v-model="payload.nodes[attribute.valueSubproperty.split('.')[1]]"
              @blur="onBlur(attribute.valueSubproperty)"
            />
          </v-col>
        </template>
      </admin-wrapper>
      <card-title-row translationPath="admin.links" v-bind="subTitleProps" />
      <admin-wrapper
        hide-title-row
        :disabled="loading"
        :inputs="linksInputs"
        @save="save"
        @validate="setValidation('links', $event)"
        :is-custom-saving="loading"
        :save-custom-validation="linksValid && nodesValid"
        disable-sticky-buttons
      >
        <template v-for="(attribute, idx) in linksInputs" #[attribute.nameInput]>
          <v-col cols="4" :key="idx">
            <data-input
              v-bind="attribute"
              v-model="payload.links[attribute.valueSubproperty.split('.')[1]]"
              @blur="onBlur(attribute.valueSubproperty)"
            />
          </v-col>
        </template>
      </admin-wrapper>
    </v-col>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { ranges } from '@/assets/js/modelingResults';
import AdminWrapper from '@/components/AdminWrapper';
import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
export default {
  name: 'ModelingResultsRangesSettings',
  components: {
    AdminWrapper,
    CardTitleRow,
    DataInput,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  computed: {
    ranges: get(`admin/settingsValues@:settingName`),
    settingName() {
      return `${this.type}_modeling_legend_ranges`;
    },
    nodesInputs() {
      return Object.keys(this.ranges.nodes).map(range => ({
        isCustom: true,
        name: `${this.settingName}.nodes.${range}`,
        nameInput: `${this.settingName}.nodes.${range}.input`,
        valueSubproperty: `nodes.${range}`,
        straightText: `${this.$i18n.t(`sidebar.${range}`)} [${ranges[this.type].nodes[range].unit}]`,
        dataType: {
          name: 'text',
        },
        rules: this.rangesRules,
      }));
    },
    linksInputs() {
      return Object.keys(this.ranges.links).map(range => ({
        isCustom: true,
        name: `${this.settingName}.links.${range}`,
        nameInput: `${this.settingName}.links.${range}.input`,
        valueSubproperty: `links.${range}`,
        straightText: `${this.$i18n.t(`sidebar.${range}`)} [${ranges[this.type].links[range].unit}]`,
        dataType: {
          name: 'text',
        },
        rules: this.rangesRules,
      }));
    },
    rangesRules() {
      return [
        v => {
          if (!v) {
            return this.$i18n.t('rules.required');
          }
          const numberOfSemicolons = [...v].filter(char => char === ';').length;
          if (numberOfSemicolons !== 4) {
            return this.$i18n.t('rules.fiveSemicolonValues');
          } else {
            let valuesArray = v.split(';');
            if (valuesArray.length !== 5) {
              return this.$i18n.t('rules.fiveSemicolonValues');
            }
            for (const value of valuesArray) {
              if (isNaN(value) || (!parseFloat(value) && value != '0')) {
                return this.$i18n.t('rules.fiveSemicolonValues');
              }
            }
            valuesArray = valuesArray.map(value => parseFloat(value));
            for (const [idx, value] of valuesArray.entries()) {
              if (value > valuesArray[idx + 1]) {
                return this.$i18n.t('rules.sortLowestToHighest');
              } else if (value == valuesArray[idx + 1]) {
                return this.$i18n.t('rules.repeatedValues');
              }
            }
          }
          return true;
        },
      ];
    },
    subTitleProps() {
      return {
        isSubtitle: true,
        hasIconColumn: false,
        titleClasses: this.loading ? ['text--disabled'] : [],
      };
    },
  },
  data: () => ({
    linksValid: false,
    nodesValid: false,
    loading: false,
    payload: { nodes: {}, links: {} },
  }),
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    onBlur(attribute) {
      const [layer, param] = attribute.split('.');
      if (this[`${layer}Valid`]) {
        return;
      }
      let value = this.payload[layer][param];
      for (const char of [',', '-', ' ', '/']) {
        value = value.replaceAll(char, ';');
      }
      value = value.replace(/;+/g, ';');
      let valuesArray = value.split(';').map(v => parseFloat(v) || v);

      if (
        valuesArray.every(value => {
          return !isNaN(value) && (parseFloat(value) || value == '0');
        })
      ) {
        valuesArray = valuesArray.map(value => parseFloat(value));
        valuesArray.sort((a, b) => {
          return a - b;
        });
      }

      const fixedValue = valuesArray.join(';');
      this.payload[layer] = { ...this.payload[layer], [param]: fixedValue };
    },
    setValidation(layer, value) {
      this[`${layer}Valid`] = value;
    },
    async save() {
      this.loading = true;
      try {
        await this.editSingleSetting({
          key: this.settingName,
          body: {
            value: this.payload,
          },
        });
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
        this.loading = false;
      } catch {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.payload = JSON.parse(JSON.stringify(this.ranges));
  },
};
</script>
